import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  useGetMetaBuilderAdAccountsQuery,
  useGetMetaBuilderPagesQuery,
  useGetMetaBuilderInstagramAccountsQuery,
  useGetMetaBuilderAuthStatusQuery,
  useMetaBuilderLoginMutation,
  useMetaBuilderRefreshMutation,
  useMetaBuilderDisconnectMutation,
  useGetMetaBuilderSavedPixelsQuery,
  useGetMetaBuilderPixelsQuery,
  useCreateMetaBuilderPixelMutation,
  useUpdateMetaBuilderPixelMutation,
} from "../apis/metaBuilderApi";
import { ROUTES } from "../consts/routes.consts";
import { emptyMetaBuilderSplitApi as api } from "../apis/emptyMetaBuilderApi";
import cancelOrderIcon from "../../assets/images/svg/cancel-order.svg";
import disconnectIcon from "../../assets/images/svg/disconnect-button.svg";
import vector4 from "../../assets/images/svg/refresh-button.svg";
import facebookConnectIcon from "../../assets/images/svg/facebook-connect.svg";
import agnleDownSmall from "../../assets/images/svg/angle-down-small-4.svg";
import metaStep1 from "../../assets/images/svg/meta-step1.svg";

import PixelCapiTrackingSection from "../metaAds/PixelCapiTrackingSection";
import MetaAdsFullGuide from "../metaAds/MetaAdsFullGuide";
import MetaAdsCapiTokenConfirmModal from "../meta-ads-capi-token-confirm-modal";
import guideMark from "../../assets/images/svg/guidemark-icon.svg";
// Storage keys (same as meta-ads-builder-public.tsx)
const MODAL_RECONNECT_FLAG_KEY = "metaAdsModalReconnectInPlace";

const safeParseJson = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    console.error("Failed to parse JSON from storage", e);
    return null;
  }
};

const normalizePageSelection = (page: any) => {
  const id = page?.id || page?.pageId;
  if (!id) return null;
  return {
    id,
    name: page?.name || page?.pageName || "",
    picture: page?.picture
      ? page.picture
      : page?.image
        ? { data: { url: page.image } }
        : undefined,
  };
};

const normalizeAdAccountSelection = (account: any) => {
  const id = account?.id || account?.account_id || account?.accountId;
  if (!id) return null;
  const cleanedName = (account?.name || "")
    .replace(/\s*\[(?:act_[^\]]+|\d+)\]/g, "")
    .replace(/^\s*(?:act_)?\d+\s+/, "")
    .trim();
  return {
    id,
    name: cleanedName || account?.name || "",
    account_id: account?.account_id || account?.accountId || undefined,
  };
};

const normalizeInstagramSelection = (instagram: any) => {
  const id = instagram?.id || instagram?.instagramId;
  if (!id) return null;
  return {
    id,
    name: instagram?.name || instagram?.username || "",
    profile_picture_url:
      instagram?.profile_picture_url || instagram?.image || undefined,
    username: instagram?.username || undefined,
  };
};

const normalizePixelSelection = (pixel: any) => {
  const id = pixel?.id || pixel?.pixelId;
  if (!id) return null;
  return {
    id,
    name: pixel?.name || "",
  };
};

type EditType = "adAccount" | "page" | "instagram";

interface MetaBuilderEditAccountModalProps {
  isOpen: boolean;
  editType: EditType | null;
  onClose: () => void;
  enableStayAndReconnect?: boolean;
  currentSelection?: {
    adAccountId?: string | null;
    pageId?: string | null;
    instagramId?: string | null;
    pixelId?: string | null;
    pageName?: string | null;
    adAccountName?: string | null;
    instagramName?: string | null;
    pixelName?: string | null;
  };
}

// Facebook blue circle icon
const FbIcon = () => (
  <div
    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
    style={{ background: "#1877f2" }}
  >
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path
        d="M6.2 1.5H5.1C4.55 1.5 4.2 1.85 4.2 2.4V3.3H3.2V4.6H4.2V8H5.6V4.6H6.5L6.8 3.3H5.6V2.55C5.6 2.25 5.75 2 6.2 2V1.5Z"
        fill="white"
      />
    </svg>
  </div>
);

// Chevron down icon
const ChevronDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    className="flex-shrink-0"
  >
    <path
      d="M3.5 5.25L7 8.75L10.5 5.25"
      stroke="rgba(255,255,255,0.45)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom dropdown component — renders a visible box with selected text + opens a list
function CustomSelect({
  label,
  subLabel,
  value,
  options,
  isLoading,
  onChange,
  getLabel,
  getId,
  placeholder,
  showFbIcon = false,
}: {
  label: string;
  subLabel?: string;
  value: string | null;
  options: any[];
  isLoading?: boolean;
  onChange: (id: string) => void;
  getLabel: (item: any) => string;
  getId: (item: any) => string;
  placeholder?: string;
  showFbIcon?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => getId(o) === value);
  const displayText = selected ? getLabel(selected) : "";

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex flex-col gap-[6px]" ref={ref}>
      <label
        className="text-[13px]"
        style={{ color: "rgba(255,255,255,0.85)" }}
      >
        {label}
      </label>

      {/* Trigger box */}
      <div
        onClick={() => !isLoading && setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-[10px] px-3 py-[10px] cursor-pointer select-none relative"
        style={{
          background: "#181c28",
          border: "1px solid #2a3050",
          minHeight: "42px",
        }}
      >
        {showFbIcon && <FbIcon />}

        <span
          className="flex-1 text-[13px] truncate"
          style={{ color: displayText ? "#ffffff" : "rgba(255,255,255,0.35)" }}
        >
          {isLoading ? "Loading..." : displayText || placeholder || "Select"}
        </span>

        {isLoading ? (
          <CircularProgress size={12} sx={{ color: "rgba(255,255,255,0.4)" }} />
        ) : (
          <ChevronDown />
        )}
      </div>

      {/* Dropdown list */}
      {open && !isLoading && (
        <div
          className="absolute z-[10001] mt-1 rounded-[10px] overflow-hidden overflow-y-auto"
          style={{
            background: "#181c28",
            border: "1px solid #2a3050",
            maxHeight: "180px",
            width: "100%",
            top: "100%",
            left: 0,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {options.length === 0 ? (
            <div
              className="px-3 py-3 text-[13px]"
              style={{ color: "rgba(255,255,255,0.4)" }}
            >
              No options available
            </div>
          ) : (
            options.map((opt) => {
              const id = getId(opt);
              const isSelected = id === value;
              return (
                <div
                  key={id}
                  onClick={() => {
                    onChange(id);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-[10px] cursor-pointer text-[13px]"
                  style={{
                    color: isSelected ? "#58F2A9" : "#ffffff",
                    background: isSelected
                      ? "rgba(88,242,169,0.08)"
                      : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background =
                        "rgba(255,255,255,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background =
                        "transparent";
                  }}
                >
                  {showFbIcon && <FbIcon />}
                  <span className="truncate">{getLabel(opt)}</span>
                </div>
              );
            })
          )}
        </div>
      )}

      {subLabel && (
        <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.3)" }}>
          {subLabel}
        </p>
      )}
    </div>
  );
}

export default function MetaBuilderEditAccountModal({
  isOpen,
  editType,
  onClose,
  enableStayAndReconnect = false,
  currentSelection,
}: MetaBuilderEditAccountModalProps) {
  const dispatch = useDispatch();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string | null>(
    null,
  );
  const [selectedInstagramId, setSelectedInstagramId] = useState<string | null>(
    null,
  );
  const [selectedPixelId, setSelectedPixelId] = useState<string | null>(null);

  // Full object states for localStorage persistence
  const [selectedPage, setSelectedPage] = useState<{
    id: string;
    name: string;
    picture?: { data?: { url?: string } };
  } | null>(null);

  const [selectedAdAccount, setSelectedAdAccount] = useState<{
    id: string;
    name: string;
    account_id?: string;
  } | null>(null);

  const [selectedInstagram, setSelectedInstagram] = useState<{
    id: string;
    name: string;
    profile_picture_url?: string;
    username?: string;
  } | null>(null);

  const [selectedPixel, setSelectedPixel] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Refs to prevent infinite loops in useEffect
  const isUpdatingPageRef = useRef(false);
  const isUpdatingAdAccountRef = useRef(false);
  const isUpdatingInstagramRef = useRef(false);
  const isUpdatingPixelRef = useRef(false);
  const isHydratingSelectionsRef = useRef(false);
  const hasInitializedForCurrentOpenRef = useRef(false);
  const previousAdAccountIdRef = useRef<string | null>(null);

  const [isCapiHowToOpen, setIsCapiHowToOpen] = useState(false);
  const [isCapiTokenConfirmOpen, setIsCapiTokenConfirmOpen] = useState(false);
  const [editingPixelData, setEditingPixelData] = useState<{
    name: string;
    type: string;
    id: string;
    capiToken: string;
    pixelId: string;
  } | null>(null);
  const [isLoadingPixelsForNewAccount, setIsLoadingPixelsForNewAccount] =
    useState(false);
  const loadingPixelsForAdAccountIdRef = useRef<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isReconnectMode, setIsReconnectMode] = useState(false);

  // Dropdown open/close states
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [isAdAccountDropdownOpen, setIsAdAccountDropdownOpen] = useState(false);
  const [isInstagramDropdownOpen, setIsInstagramDropdownOpen] = useState(false);
  const [isPixelDropdownOpen, setIsPixelDropdownOpen] = useState(false);

  // Instagram retry logic
  const [isRetryingInstagram, setIsRetryingInstagram] = useState(false);
  const [instagramRetryCount, setInstagramRetryCount] = useState(0);
  const MAX_INSTAGRAM_RETRIES = 3;
  const instagramEmptyRefetchDoneRef = useRef<string | null>(null);

  const [isAccountGuideModalOpen, setIsAccountGuideModalOpen] = useState(false);

  // Manage body scroll when the edit modal is open (prevents background scroll / scroll chaining)
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  // Manage body scroll + Esc close when full guide modal is open
  useEffect(() => {
    if (!isAccountGuideModalOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsAccountGuideModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountGuideModalOpen]);

  const { data: authStatusData, refetch: refetchAuthStatus } =
    useGetMetaBuilderAuthStatusQuery(undefined, { skip: !isOpen });

  const { data: savedPixelsData, isLoading: isLoadingPixels } =
    useGetMetaBuilderSavedPixelsQuery(
      { facebookUserId: authStatusData?.facebookUserId },
      { skip: !isOpen || !authStatusData?.facebookUserId },
    );

  const [metaAdsRefresh, { isLoading: isRefreshing }] =
    useMetaBuilderRefreshMutation();
  const [metaAdsLogin, { isLoading: isLoginLoading }] =
    useMetaBuilderLoginMutation();
  const [metaAdsDisconnect, { isLoading: isDisconnecting }] =
    useMetaBuilderDisconnectMutation();

  const {
    data: adAccountsData,
    isLoading: isLoadingAdAccounts,
    isFetching: isAdAccountsFetching,
  } = useGetMetaBuilderAdAccountsQuery(
    { pageId: selectedPageId || "" },
    { skip: !isOpen || !selectedPageId || !authStatusData?.connected },
  );

  const { data: pagesData, isLoading: isLoadingPages } =
    useGetMetaBuilderPagesQuery(undefined, { skip: !isOpen });

  const {
    data: instagramData,
    isLoading: isLoadingInstagram,
    isFetching: isInstagramFetching,
    isError: isInstagramError,
    refetch: refetchInstagramAccounts,
  } = useGetMetaBuilderInstagramAccountsQuery(
    { pageId: selectedPageId || "" },
    { skip: !isOpen || !selectedPageId || !authStatusData?.connected },
  );

  // Get Facebook user ID from session storage or from auth status
  const facebookUserId = useMemo(() => {
    try {
      return (
        authStatusData?.facebookUserId ||
        sessionStorage.getItem("facebookUserId") ||
        undefined
      );
    } catch (e) {
      console.error("Failed to get Facebook user ID from session storage:", e);
      return undefined;
    }
  }, [authStatusData?.facebookUserId]);

  // Query for pixels based on selected ad account
  const {
    data: adAccountPixelsData,
    isLoading: isLoadingAdAccountPixels,
    isFetching: isFetchingAdAccountPixels,
    refetch: refetchAdAccountPixels,
  } = useGetMetaBuilderPixelsQuery(
    {
      adAccountId: selectedAdAccountId || "",
      facebookUserId,
    },
    {
      skip: !isOpen || !selectedAdAccountId || !facebookUserId,
    },
  );

  // Pixel mutations
  const [createMetaAdsPixel, { isLoading: isCreatingPixel }] =
    useCreateMetaBuilderPixelMutation();
  const [updateMetaAdsPixel, { isLoading: isUpdatingPixel }] =
    useUpdateMetaBuilderPixelMutation();

  useEffect(() => {
    if (!isOpen) return;
    if (!authStatusData?.connected) return;
    setIsReconnectMode(false);
    try {
      sessionStorage.removeItem(MODAL_RECONNECT_FLAG_KEY);
    } catch (err) {
      console.error("Failed to clear modal reconnect flag after connect:", err);
    }
  }, [isOpen, authStatusData?.connected]);

  useEffect(() => {
    if (!isOpen) {
      // Clear global modal state when closed
      (window as any).metaBuilderModalOpen = false;
      hasInitializedForCurrentOpenRef.current = false;
      setIsReconnectMode(false);
      try {
        sessionStorage.removeItem(MODAL_RECONNECT_FLAG_KEY);
      } catch (err) {
        console.error("Failed to clear modal reconnect flag:", err);
      }
      return;
    }

    if (enableStayAndReconnect) {
      try {
        setIsReconnectMode(
          sessionStorage.getItem(MODAL_RECONNECT_FLAG_KEY) === "true",
        );
      } catch (err) {
        console.error("Failed to read modal reconnect flag:", err);
      }
    }

    if (hasInitializedForCurrentOpenRef.current) return;
    hasInitializedForCurrentOpenRef.current = true;

    isHydratingSelectionsRef.current = true;

    const accountData = safeParseJson<any>(
      localStorage.getItem("metaAdsAccountData"),
    );

    const currentPage = normalizePageSelection(
      currentSelection?.pageId
        ? { id: currentSelection.pageId, name: currentSelection.pageName }
        : null,
    );
    const currentAdAccount = normalizeAdAccountSelection(
      currentSelection?.adAccountId
        ? {
            id: currentSelection.adAccountId,
            name: currentSelection.adAccountName,
          }
        : null,
    );
    const currentInstagram = normalizeInstagramSelection(
      currentSelection?.instagramId
        ? {
            id: currentSelection.instagramId,
            name: currentSelection.instagramName,
          }
        : null,
    );
    const currentPixel = normalizePixelSelection(
      currentSelection?.pixelId
        ? {
            id: currentSelection.pixelId,
            name: currentSelection.pixelName,
          }
        : null,
    );

    const resolvedPage =
      currentPage || normalizePageSelection(accountData?.page) || selectedPage;
    const resolvedAdAccount =
      currentAdAccount ||
      normalizeAdAccountSelection(accountData?.adAccount) ||
      selectedAdAccount;
    const resolvedInstagram =
      currentInstagram ||
      normalizeInstagramSelection(accountData?.instagram) ||
      selectedInstagram;
    const resolvedPixel =
      currentPixel ||
      normalizePixelSelection(accountData?.pixel) ||
      selectedPixel;

    if (resolvedPage?.id) setSelectedPageId(resolvedPage.id);
    if (resolvedAdAccount?.id) setSelectedAdAccountId(resolvedAdAccount.id);
    if (resolvedInstagram?.id) setSelectedInstagramId(resolvedInstagram.id);
    if (resolvedPixel?.id) setSelectedPixelId(resolvedPixel.id);

    if (resolvedPage) setSelectedPage(resolvedPage);
    if (resolvedAdAccount) setSelectedAdAccount(resolvedAdAccount);
    if (resolvedInstagram) setSelectedInstagram(resolvedInstagram);
    if (resolvedPixel) setSelectedPixel(resolvedPixel);

    if (resolvedAdAccount?.id) {
      previousAdAccountIdRef.current = resolvedAdAccount.id;
    }

    // Set global modal state
    (window as any).metaBuilderModalOpen = true;

    const timer = setTimeout(() => {
      isHydratingSelectionsRef.current = false;
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen, currentSelection, enableStayAndReconnect]);

  // Cleanup global state on unmount
  useEffect(() => {
    return () => {
      (window as any).metaBuilderModalOpen = false;
      try {
        sessionStorage.removeItem(MODAL_RECONNECT_FLAG_KEY);
      } catch (err) {
        console.error("Failed to clear modal reconnect flag on unmount:", err);
      }
    };
  }, []);

  // Auto-select first page after reconnect when no page is selected
  // useEffect(() => {
  //   if (!isOpen) return;
  //   if (!authStatusData?.connected) return;
  //   if (!pagesData?.pages || pagesData.pages.length === 0) return;
  //   if (selectedPageId) return; // Already has a page selected

  //   // Check if localStorage is empty (indicating fresh reconnect)
  //   const hasStoredData =
  //     localStorage.getItem(PAGE_STORAGE_KEY) ||
  //     localStorage.getItem(AD_ACCOUNT_STORAGE_KEY) ||
  //     localStorage.getItem(INSTAGRAM_STORAGE_KEY) ||
  //     localStorage.getItem(PIXEL_STORAGE_KEY);

  //   if (hasStoredData) return; // Has stored data, use normal flow

  //   // Only auto-select first page after fresh reconnect (localStorage is empty)
  //   const firstPage = pagesData.pages[0];
  //   setSelectedPageId(firstPage.id);
  //   setSelectedPage(firstPage);

  //   // Reset the skip hydration flag after data is loaded and first page is auto-selected
  //   skipHydrationRef.current = false;
  // }, [isOpen, authStatusData?.connected, pagesData?.pages]);

  // Reset pixel when ad account changes and load new pixel if exists
  useEffect(() => {
    if (!selectedAdAccountId) return;
    if (!isOpen) return;
    if (isHydratingSelectionsRef.current) return;

    const prevAdAccountId = previousAdAccountIdRef.current;
    if (!prevAdAccountId) {
      previousAdAccountIdRef.current = selectedAdAccountId;
      return;
    }
    if (prevAdAccountId === selectedAdAccountId) return;

    // Keep existing values; only refresh pixel list for the newly selected ad account.
    setIsLoadingPixelsForNewAccount(true);
    loadingPixelsForAdAccountIdRef.current = selectedAdAccountId;
    previousAdAccountIdRef.current = selectedAdAccountId;
  }, [isOpen, selectedAdAccountId]);

  // Sync page ID with full object state
  useEffect(() => {
    if (isUpdatingPageRef.current) {
      isUpdatingPageRef.current = false;
      return;
    }
    if (selectedPageId && pagesData?.pages) {
      const page = pagesData.pages.find((p: any) => p.id === selectedPageId);
      if (page && page.id !== selectedPage?.id) {
        isUpdatingPageRef.current = true;
        setSelectedPage(page);
      }
    }
  }, [selectedPageId, pagesData?.pages, selectedPage?.id]);

  // Sync ad account ID with full object state
  useEffect(() => {
    if (isUpdatingAdAccountRef.current) {
      isUpdatingAdAccountRef.current = false;
      return;
    }
    if (selectedAdAccountId && adAccountsData?.adAccounts) {
      const account = adAccountsData.adAccounts.find(
        (a: any) => a.id === selectedAdAccountId,
      );
      if (account && account.id !== selectedAdAccount?.id) {
        isUpdatingAdAccountRef.current = true;
        setSelectedAdAccount(account);
      }
    }
  }, [selectedAdAccountId, adAccountsData?.adAccounts, selectedAdAccount?.id]);

  // Sync Instagram ID with full object state
  useEffect(() => {
    if (isUpdatingInstagramRef.current) {
      isUpdatingInstagramRef.current = false;
      return;
    }
    if (selectedInstagramId && instagramData?.instagramAccounts) {
      const ig = instagramData.instagramAccounts.find(
        (ig: any) => ig.id === selectedInstagramId,
      );
      if (ig && ig.id !== selectedInstagram?.id) {
        isUpdatingInstagramRef.current = true;
        setSelectedInstagram({
          id: ig.id,
          name: ig.name || ig.username || "",
          profile_picture_url: ig.profile_picture_url,
          username: ig.username,
        });
      }
    }
  }, [
    selectedInstagramId,
    instagramData?.instagramAccounts,
    selectedInstagram?.id,
  ]);

  // Sync pixel ID with full object state
  useEffect(() => {
    if (isUpdatingPixelRef.current) {
      isUpdatingPixelRef.current = false;
      return;
    }
    if (selectedPixelId && adAccountPixelsData?.pixels) {
      const pixel = adAccountPixelsData.pixels.find(
        (p: any) => (p.id || p.pixelId) === selectedPixelId,
      );
      if (pixel && (pixel.id || pixel.pixelId) !== selectedPixel?.id) {
        isUpdatingPixelRef.current = true;
        const pixelId = pixel.id || pixel.pixelId;
        if (pixelId) {
          setSelectedPixel({
            id: pixelId,
            name: pixel.name || "",
          });
        }
      }
    }
  }, [selectedPixelId, adAccountPixelsData?.pixels, selectedPixel?.id]);

  // Persist selections to localStorage whenever they change
  const persistSelections = () => {
    try {
      const accountData = safeParseJson<any>(
        localStorage.getItem("metaAdsAccountData"),
      );

      const resolvedPage =
        selectedPage || normalizePageSelection(accountData?.page);
      const resolvedAdAccount =
        selectedAdAccount ||
        normalizeAdAccountSelection(accountData?.adAccount);
      const resolvedInstagram =
        selectedInstagram ||
        normalizeInstagramSelection(accountData?.instagram);
      const resolvedPixel =
        selectedPixel || normalizePixelSelection(accountData?.pixel);
      const cleanedAdAccountName = (resolvedAdAccount?.name || "")
        .replace(/\s*\[(?:act_[^\]]+|\d+)\]/g, "")
        .replace(/^\s*(?:act_)?\d+\s+/, "")
        .trim();

      const payload = {
        page: resolvedPage
          ? {
              name: resolvedPage.name ?? "",
              id: resolvedPage.id ?? "",
              image: resolvedPage.picture?.data?.url ?? null,
            }
          : null,
        adAccount: resolvedAdAccount
          ? {
              name: cleanedAdAccountName || resolvedAdAccount.name || "",
              id: resolvedAdAccount.id || resolvedAdAccount.account_id || "",
            }
          : null,
        instagram: resolvedInstagram
          ? {
              name: resolvedInstagram.name ?? "",
              id: resolvedInstagram.id ?? "",
              image: resolvedInstagram.profile_picture_url ?? null,
            }
          : null,
        pixel: resolvedPixel
          ? {
              name: resolvedPixel.name ?? "",
              id: resolvedPixel.id ?? "",
            }
          : null,
      };

      localStorage.setItem("metaAdsAccountData", JSON.stringify(payload));

      // Dispatch custom event to notify other components (like meta-mobile-ad-preview)
      window.dispatchEvent(new Event("metaAdsSelectionsChanged"));
    } catch (e) {
      console.error("Failed to persist meta ads selections", e);
    }
  };

  // Call persistSelections whenever any selection changes
  useEffect(() => {
    if (!isOpen) return;
    persistSelections();
  }, [
    isOpen,
    selectedPage,
    selectedAdAccount,
    selectedInstagram,
    selectedPixel,
  ]);

  // Retry Instagram accounts on error (up to MAX_INSTAGRAM_RETRIES times)
  useEffect(() => {
    if (
      isInstagramError &&
      authStatusData?.connected &&
      instagramRetryCount < MAX_INSTAGRAM_RETRIES
    ) {
      const timer = setTimeout(() => {
        console.log(
          `Retrying Instagram API... Attempt ${instagramRetryCount + 1} of ${MAX_INSTAGRAM_RETRIES}`,
        );
        setIsRetryingInstagram(true);
        refetchInstagramAccounts()
          .then(() => {
            setInstagramRetryCount((prev) => prev + 1);
          })
          .finally(() => {
            setIsRetryingInstagram(false);
          });
      }, 2000); // Retry after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [
    isInstagramError,
    authStatusData?.connected,
    instagramRetryCount,
    refetchInstagramAccounts,
  ]);

  // Refetch Instagram accounts once when result is empty (transient/cache recovery)
  useEffect(() => {
    const pageId = selectedPageId;
    if (!pageId || !authStatusData?.connected) return;

    const accounts =
      instagramData?.instagramAccounts ||
      instagramData?.accounts ||
      instagramData?.instagram_accounts;
    const isEmpty = !accounts || accounts.length === 0;
    const isSettled =
      !isLoadingInstagram &&
      !isRetryingInstagram &&
      !isInstagramFetching &&
      !isInstagramError;

    if (
      isEmpty &&
      isSettled &&
      instagramEmptyRefetchDoneRef.current !== pageId
    ) {
      const timer = setTimeout(() => {
        instagramEmptyRefetchDoneRef.current = pageId;
        refetchInstagramAccounts();
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [
    selectedPageId,
    authStatusData?.connected,
    instagramData?.instagramAccounts,
    instagramData?.accounts,
    instagramData?.instagram_accounts,
    isLoadingInstagram,
    isRetryingInstagram,
    isInstagramFetching,
    isInstagramError,
    refetchInstagramAccounts,
  ]);

  // Reset Instagram retry state when page changes
  useEffect(() => {
    setInstagramRetryCount(0);
    setIsRetryingInstagram(false);
    instagramEmptyRefetchDoneRef.current = null;
  }, [selectedPageId]);

  // Clear loading state when pixels data is loaded for the new ad account
  useEffect(() => {
    if (
      isLoadingPixelsForNewAccount &&
      !isFetchingAdAccountPixels &&
      selectedAdAccountId &&
      loadingPixelsForAdAccountIdRef.current === selectedAdAccountId
    ) {
      setIsLoadingPixelsForNewAccount(false);
      loadingPixelsForAdAccountIdRef.current = null;
    }
  }, [
    isFetchingAdAccountPixels,
    selectedAdAccountId,
    isLoadingPixelsForNewAccount,
  ]);

  // Check if selected pixel is already in saved pixels
  const isPixelAlreadySaved = useMemo(() => {
    if (!selectedPixelId || !savedPixelsData?.pixels) return false;
    return savedPixelsData.pixels.some(
      (savedPixel) =>
        savedPixel.pixelId === selectedPixelId ||
        savedPixel._id === selectedPixelId ||
        savedPixel.id === selectedPixelId,
    );
  }, [selectedPixelId, savedPixelsData?.pixels]);

  const selectedSavedPixelForTracking = useMemo(() => {
    if (!selectedPixelId || !savedPixelsData?.pixels) return null;
    return (
      savedPixelsData.pixels.find(
        (savedPixel: any) =>
          savedPixel.pixelId === selectedPixelId ||
          savedPixel._id === selectedPixelId ||
          savedPixel.id === selectedPixelId,
      ) || null
    );
  }, [selectedPixelId, savedPixelsData?.pixels]);

  // Keep editable CAPI state in sync with selected pixel / saved pixel
  useEffect(() => {
    if (!selectedPixelId) {
      setEditingPixelData(null);
      return;
    }
    const recordId =
      (selectedSavedPixelForTracking as any)?._id ||
      (selectedSavedPixelForTracking as any)?.id ||
      "";
    setEditingPixelData((prev) => ({
      name:
        (selectedSavedPixelForTracking as any)?.name ||
        prev?.name ||
        selectedPixel?.name ||
        "",
      type:
        (selectedSavedPixelForTracking as any)?.type ||
        prev?.type ||
        "Facebook Ads [Pixel + CAPI Token]",
      id: selectedPixelId,
      capiToken:
        (selectedSavedPixelForTracking as any)?.capiToken ??
        prev?.capiToken ??
        "",
      pixelId: recordId,
    }));
  }, [selectedPixelId, selectedSavedPixelForTracking, selectedPixel?.name]);

  const handleRefresh = async () => {
    try {
      await metaAdsRefresh().unwrap();

      const { authURL } = await metaAdsLogin().unwrap();
      if (!authURL) throw new Error("Auth URL missing");

      // Open centered popup
      const width = 600;
      const height = 750;
      const popup = window.open(
        authURL,
        "metaAdsAuthPopup",
        `width=${width},height=${height},
       left=${(window.screen.width - width) / 2},
       top=${(window.screen.height - height) / 2}`,
      );

      if (!popup) throw new Error("Popup blocked");
      setIsPopupOpen(true);

      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type !== "FB_REFRESH_DONE") return;

        const isConnected =
          event.data.connected === true || event.data.connected === "true";

        popup.close();
        window.removeEventListener("message", handleMessage);
        setIsPopupOpen(false);

        if (!isConnected) {
          return;
        }

        // Clear storage when popup closes after successful connection
        try {
          ["facebookUserId"].forEach((key) => sessionStorage.removeItem(key));
        } catch (err) {
          console.error("Storage clear failed:", err);
        }

        // Clear dropdown values
        setSelectedPageId(null);
        setSelectedAdAccountId(null);
        setSelectedInstagramId(null);
        setSelectedPixelId(null);
        setSelectedPage(null);
        setSelectedAdAccount(null);
        setSelectedInstagram(null);
        setSelectedPixel(null);

        // Save Facebook user ID from message
        if (event.data?.facebookUserId) {
          try {
            sessionStorage.setItem("facebookUserId", event.data.facebookUserId);
            console.log("Facebook User ID saved:", event.data.facebookUserId);
          } catch (e) {
            console.error("Failed to save Facebook user ID:", e);
          }
        }

        toast.success("Facebook access refreshed successfully!");

        // Invalidate cache to force refetch all meta builder data
        dispatch(
          api.util.invalidateTags([
            "MetaAdsAuth",
            "MetaAdsPages",
            "MetaAdsInstagramAccounts",
            "MetaAdsAdAccounts",
            "MetaAdsPixels",
            "MetaAdsSavedPixels",
          ]),
        );

        // Refetch data to get updated pages/accounts
        refetchAuthStatus();
      };

      window.addEventListener("message", handleMessage);
    } catch (error) {
      console.error(error);
      toast.error("Failed to refresh Facebook access");
      setIsPopupOpen(false);
    }
  };

  const handleReconnectInPlace = async () => {
    try {
      const { authURL } = await metaAdsLogin().unwrap();
      if (!authURL) throw new Error("Auth URL missing");

      const width = 600;
      const height = 750;
      const popup = window.open(
        authURL,
        "metaAdsAuthPopup",
        `width=${width},height=${height},
       left=${(window.screen.width - width) / 2},
       top=${(window.screen.height - height) / 2}`,
      );

      if (!popup) throw new Error("Popup blocked");
      setIsPopupOpen(true);

      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type !== "FB_REFRESH_DONE") return;

        const isConnected =
          event.data.connected === true || event.data.connected === "true";

        popup.close();
        window.removeEventListener("message", handleMessage);
        setIsPopupOpen(false);

        if (!isConnected) return;

        if (event.data?.facebookUserId) {
          try {
            sessionStorage.setItem("facebookUserId", event.data.facebookUserId);
          } catch (err) {
            console.error("Failed to save Facebook user ID:", err);
          }
        }

        // After reconnect, reset old selections so stale values are not shown.
        try {
          localStorage.removeItem("metaAdsAccountData");
        } catch (err) {
          console.error(
            "Failed to clear old meta selections after reconnect:",
            err,
          );
        }

        previousAdAccountIdRef.current = null;
        loadingPixelsForAdAccountIdRef.current = null;
        setIsLoadingPixelsForNewAccount(false);
        setEditingPixelData(null);
        setSelectedPageId(null);
        setSelectedAdAccountId(null);
        setSelectedInstagramId(null);
        setSelectedPixelId(null);
        setSelectedPage(null);
        setSelectedAdAccount(null);
        setSelectedInstagram(null);
        setSelectedPixel(null);

        setIsReconnectMode(false);
        try {
          sessionStorage.removeItem(MODAL_RECONNECT_FLAG_KEY);
        } catch (err) {
          console.error("Failed to clear modal reconnect flag:", err);
        }

        toast.success("Facebook connected successfully!");

        dispatch(
          api.util.invalidateTags([
            "MetaAdsAuth",
            "MetaAdsPages",
            "MetaAdsInstagramAccounts",
            "MetaAdsAdAccounts",
            "MetaAdsPixels",
            "MetaAdsSavedPixels",
          ]),
        );
        refetchAuthStatus();
      };

      window.addEventListener("message", handleMessage);
    } catch (error) {
      console.error(error);
      toast.error("Failed to connect Facebook");
      setIsPopupOpen(false);
    }
  };

  const handleDisconnect = async () => {
    if (enableStayAndReconnect) {
      try {
        sessionStorage.setItem(MODAL_RECONNECT_FLAG_KEY, "true");
      } catch (err) {
        console.error("Failed to set modal reconnect flag:", err);
      }
    }

    try {
      await metaAdsDisconnect().unwrap();

      if (enableStayAndReconnect) {
        setIsReconnectMode(true);
        toast.success("Account disconnected. Reconnect Facebook to continue.");
        dispatch(
          api.util.invalidateTags([
            "MetaAdsAuth",
            "MetaAdsPages",
            "MetaAdsInstagramAccounts",
            "MetaAdsAdAccounts",
            "MetaAdsPixels",
            "MetaAdsSavedPixels",
          ]),
        );
        refetchAuthStatus();
        return;
      }
      toast.success("Account disconnected successfully");
      onClose();
      setTimeout(() => {
        window.location.href = "/meta-ads-builder";
      }, 300);
    } catch (error: any) {
      if (enableStayAndReconnect) {
        try {
          sessionStorage.removeItem(MODAL_RECONNECT_FLAG_KEY);
        } catch (err) {
          console.error("Failed to clear modal reconnect flag:", err);
        }
      }
      toast.error(error?.data?.msg || "Failed to disconnect account");
    }
  };

  const handleCapiTokenChange = (token: string) => {
    setEditingPixelData((prev) =>
      prev ? { ...prev, capiToken: token } : prev,
    );
  };

  const handleSaveForFutureUse = async (): Promise<boolean> => {
    if (!selectedAdAccountId) {
      toast.error("Please select an ad account first.");
      return false;
    }
    if (!selectedPixelId || !selectedPixel) {
      toast.error("Please select a pixel first.");
      return false;
    }

    const name = (editingPixelData?.name || selectedPixel.name || "").trim();
    if (!name) {
      toast.error("Pixel name is required.");
      return false;
    }

    const token = editingPixelData?.capiToken?.trim() || "";

    try {
      const savedRecordId =
        (selectedSavedPixelForTracking as any)?._id ||
        (selectedSavedPixelForTracking as any)?.id ||
        "";

      if (savedRecordId) {
        await updateMetaAdsPixel({
          pixelId: savedRecordId,
          name,
          type: "Facebook Ads [Pixel + CAPI Token]",
          capiToken: token === "" ? "" : token,
          adAccountId: selectedAdAccountId,
        }).unwrap();
      } else {
        await createMetaAdsPixel({
          name,
          type: "Facebook Ads [Pixel + CAPI Token]",
          pixelId: selectedPixelId,
          capiToken: token === "" ? "" : token,
          adAccountId: selectedAdAccountId,
        }).unwrap();
      }

      toast.success("Saved for future use!");
      return true;
    } catch (error: any) {
      console.error("Error saving pixel:", error);
      toast.error(
        error?.data?.msg ||
          error?.message ||
          "Failed to save. Please try again.",
      );
      return false;
    }
  };

  if (!isOpen) return null;

  const pages = pagesData?.pages || [];
  const adAccounts = adAccountsData?.adAccounts || [];
  const instagramAccounts =
    instagramData?.instagramAccounts ||
    instagramData?.accounts ||
    instagramData?.instagram_accounts ||
    [];
  const savedPixels = savedPixelsData?.pixels || [];
  const selectedPageFromList = pages.find((p: any) => p.id === selectedPageId);
  const selectedPageDisplay = selectedPageFromList || selectedPage;
  const selectedAdAccountFromList = adAccounts.find(
    (a: any) => a.id === selectedAdAccountId,
  );
  const selectedAdAccountDisplay =
    selectedAdAccountFromList || selectedAdAccount;
  const selectedInstagramFromList = instagramAccounts.find(
    (ig: any) => ig.id === selectedInstagramId,
  );
  const selectedInstagramDisplay =
    selectedInstagramFromList || selectedInstagram;
  const selectedPixelFromList = adAccountPixelsData?.pixels?.find(
    (p: any) => (p.id || p.pixelId) === selectedPixelId,
  );
  const selectedPixelDisplay = selectedPixelFromList || selectedPixel;
  const isFacebookConnected = Boolean(authStatusData?.connected);
  const showInlineReconnect =
    enableStayAndReconnect && (isReconnectMode || !isFacebookConnected);
  const disableTopActions =
    isRefreshing || isLoginLoading || isDisconnecting || isPopupOpen;
  const shouldRedirectOnClose =
    enableStayAndReconnect && isReconnectMode && !isFacebookConnected;

  const handleModalClose = () => {
    onClose();
    if (shouldRedirectOnClose) {
      window.location.href = ROUTES.META_ADS_SETUP_PUBLIC;
    }
  };

  const modalContent = (
    <>
      <MetaAdsCapiTokenConfirmModal
        isOpen={isCapiTokenConfirmOpen}
        isLoading={isCreatingPixel || isUpdatingPixel}
        onClose={() => setIsCapiTokenConfirmOpen(false)}
        onConfirm={handleSaveForFutureUse}
      />
      {/* Modal backdrop - covers entire viewport including header */}
      <div
        className="fixed inset-0 z-[100000] flex items-center justify-center lg:p-4 sm:p-6 px-2 py-4 overflow-hidden overscroll-none"
        style={{
          background: "rgba(0,0,0,0.78)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
      >
        <div className="relative w-full max-w-[1000px] h-[calc(100dvh-32px)] sm:h-auto sm:max-h-[calc(100dvh-48px)] lg:max-h-[92vh] z-[100001] rounded-[30px] p-[2px] bg-[linear-gradient(0deg,#FFFFFF_0%,#001AFF_44.71%,#1B76FF_100%)] sm:bg-none shadow-[0px_10px_40px_0px_#060C1180]">
          <div
            className="relative w-full h-full rounded-[28px] bg-[#0e0e10] flex flex-col overflow-y-auto overscroll-contain overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* Teal radial top glow — matches screenshot */}
            <div
              className="absolute left-1/2 -translate-x-1/2 opacity-80 blur-2xl sm:blur-3xl w-[280px] h-[90px] sm:w-[360px] sm:h-[110px] md:w-[460px] md:h-[140px]"
              style={{
                top: "-50px",
                background:
                  "linear-gradient(226.45deg, #1B76FF 12.8%, #2BFAA3 93.65%)",
              }}
            />

            <div className="relative z-10 lg:px-7 px-6 pt-[30px] pb-[30px]">
              <button onClick={handleModalClose} aria-label="Close">
                <img
                  src={cancelOrderIcon}
                  alt="Close"
                  className="w-[26px] h-[26px] absolute lg:right-16 lg:top-8 right-6 top-8"
                />
              </button>

              {/* ── Step 2: Account Setup (Exact design from main page) ── */}
              <div className="flex items-center justify-between mb-[16px] relative z-20 mt-2 lg:mt-[60px]">
                <div className="flex flex-col gap-[16px]">
                  <div className="flex items-center gap-[16px] lg:ml-[50px] pr-14 lg:pr-[5.5rem] min-w-0">
                    <h2 className="text-white font-semibold tracking-tight text-[24px] lg:text-[32px] leading-none min-w-0">
                      Account Setup
                    </h2>

                    {isFacebookConnected && (
                      <button
                        type="button"
                        onClick={() => setIsAccountGuideModalOpen(true)}
                        className="group hidden lg:inline-flex items-center gap-2 text-[16px] text-white min-w-max shrink-0 transition-colors duration-200 hover:text-gray-300"
                      >
                        <span className="shrink-0">View full guide</span>

                        <img
                          src={guideMark}
                          alt=""
                          className="w-[30px] h-[30px] shrink-0 object-contain transition-opacity duration-200 group-hover:opacity-80"
                          width={30}
                          height={30}
                        />
                      </button>
                    )}
                  </div>

                  {isFacebookConnected && (
                    <button
                      type="button"
                      onClick={() => setIsAccountGuideModalOpen(true)}
                      className="group lg:hidden inline-flex items-center gap-3 text-[16px] text-white ml-[2px] min-w-max shrink-0 transition-colors duration-200 hover:text-gray-300"
                    >
                      <span className="shrink-0">View full guide</span>

                      <img
                        src={guideMark}
                        alt=""
                        className="w-[30px] h-[30px] shrink-0 object-contain transition-opacity duration-200 group-hover:opacity-80"
                        width={30}
                        height={30}
                      />
                    </button>
                  )}
                </div>
              </div>

              {/* ── Step 2: Account Setup (Exact design from main page) ── */}
              <div className="flex flex-col w-full min-w-0 max-w-full lg:max-w-[900px] items-start gap-4 px-0 sm:px-4 md:pl-[30px] md:pr-0 lg:pl-[52px] lg:pr-0 py-0 relative flex-[0_0_auto]">
                <div className="flex flex-col lg:flex-row gap-[16px] items-start lg:items-center justify-start lg:justify-between w-full min-w-0 mb-[24px] pr-2 sm:pr-4">
                  {showInlineReconnect ? (
                    <div className="w-full ml-[-10px] flex items-center justify-start">
                      <button
                        onClick={handleReconnectInPlace}
                        disabled={isLoginLoading || isPopupOpen}
                        className="inline-flex items-center justify-center gap-2 w-full sm:w-[280px] h-[48px] px-6 py-4 bg-[#FFFFFF] rounded-[999px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoginLoading || isPopupOpen ? (
                          <CircularProgress
                            size={14}
                            sx={{ color: "#101010" }}
                          />
                        ) : (
                          <img
                            src={facebookConnectIcon}
                            alt=""
                            className="w-[30px] h-[30px] shrink-0"
                          />
                        )}
                        <span className="font-semibold text-[#000000] text-[16px]">
                          {isLoginLoading || isPopupOpen
                            ? "Connecting..."
                            : "Connect with Facebook"}
                        </span>
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={handleRefresh}
                        disabled={disableTopActions}
                        className="group flex flex-row-reverse items-center gap-2 shrink-0 text-[14px] min-w-max transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ color: "rgba(255,255,255,0.72)" }}
                      >
                        {isRefreshing || isPopupOpen ? (
                          <CircularProgress
                            size={13}
                            sx={{ color: "#ffffffb0" }}
                          />
                        ) : (
                          <img
                            src={vector4}
                            alt="Refresh"
                            className="h-[30px] w-[30px] shrink-0 object-contain transition-opacity duration-200 group-hover:opacity-80"
                            width={30}
                            height={30}
                          />
                        )}

                        <span className="text-[16px] text-[#00C3F0] transition-colors duration-200 group-hover:text-[#00a8cc] shrink-0">
                          Refresh Facebook Access
                        </span>
                      </button>

                      {/* 🔹 Right Side - Disconnect */}
                      <button
                        onClick={handleDisconnect}
                        disabled={disableTopActions}
                        className="group flex items-center gap-2 shrink-0 lg:mr-[-15px] text-[16px] min-w-max transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ color: "#e2425c" }}
                      >
                        {isDisconnecting && (
                          <CircularProgress
                            size={14}
                            sx={{ color: "#E2425C" }}
                          />
                        )}

                        <span className="shrink-0 transition-colors duration-200 group-hover:text-[#c93a50]">
                          Disconnect Profile
                        </span>

                        <img
                          src={disconnectIcon}
                          alt="Disconnect"
                          className="h-[30px] w-[30px] shrink-0 object-contain transition-opacity duration-200 group-hover:opacity-80"
                          width={30}
                          height={30}
                        />
                      </button>
                    </>
                  )}
                </div>
                {!showInlineReconnect && (
                  <>
                    {/* Dropdowns grid - stack on mobile, 2-col on lg */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
                      {/* Facebook Page */}
                      <div className="flex flex-col items-start gap-[8.4px] relative">
                        <label className="relative w-fit font-normal text-white text-sm tracking-[0] leading-[18.2px] whitespace-nowrap">
                          Select your Facebook Page
                        </label>

                        <div className="relative self-stretch w-full facebook-page-dropdown">
                          <div
                            className={`flex h-11 items-center justify-between px-[11.2px] py-[4.2px] relative self-stretch w-full bg-white rounded-[8.4px] overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${!pages || pages.length === 0 ? "cursor-default" : ""}`}
                            onClick={() =>
                              !isLoadingPages &&
                              pages &&
                              pages.length > 0 &&
                              setIsPageDropdownOpen(!isPageDropdownOpen)
                            }
                          >
                            <div className="inline-flex items-center gap-[8.4px] relative flex-[0_0_auto] min-w-0 flex-1">
                              {!isLoadingPages && selectedPageId && (
                                <div className="relative w-[19.6px] h-[19.6px] bg-[#0202021a] rounded-[700px] overflow-hidden flex items-center justify-center flex-shrink-0">
                                  {(() => {
                                    const imageUrl =
                                      selectedPageDisplay?.picture?.data?.url;
                                    return (
                                      <img
                                        className="w-full h-full object-cover"
                                        alt={
                                          selectedPageDisplay?.name ||
                                          "Selected page"
                                        }
                                        src={imageUrl}
                                      />
                                    );
                                  })()}
                                </div>
                              )}
                              <span className="relative min-w-0 truncate font-medium text-variable-collection-black text-[12px] sm:text-[11.2px] tracking-[0] leading-[normal]">
                                {isLoadingPages
                                  ? "Loading..."
                                  : selectedPageId
                                    ? selectedPageDisplay?.name || ""
                                    : pages && pages.length > 0
                                      ? "Select a page"
                                      : "No pages found"}
                              </span>
                            </div>

                            {pages && pages.length > 0 && !isLoadingPages && (
                              <img
                                className={`relative w-[22.4px] h-[22.4px] aspect-[1] transition-transform flex-shrink-0 ${isPageDropdownOpen ? "rotate-180" : ""}`}
                                alt=""
                                src={agnleDownSmall}
                              />
                            )}
                          </div>

                          {isPageDropdownOpen && pages && pages.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-[8.4px] shadow-lg z-10 max-h-[200px] overflow-y-auto">
                              {pages.map((page: any) => (
                                <div
                                  key={page.id}
                                  className={`flex items-center gap-[8.4px] px-[11.2px] py-[8px] cursor-pointer hover:bg-gray-100 ${
                                    selectedPageId === page.id
                                      ? "bg-gray-100"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    const isPageChanged =
                                      selectedPageId !== page.id;
                                    setSelectedPageId(page.id);
                                    setSelectedPage(page);
                                    setIsPageDropdownOpen(false);
                                    if (isPageChanged) {
                                      // Reset dependent selections when page changes so user can pick fresh values.
                                      setSelectedAdAccountId(null);
                                      setSelectedAdAccount(null);
                                      setSelectedInstagramId(null);
                                      setSelectedInstagram(null);
                                      setSelectedPixelId(null);
                                      setSelectedPixel(null);
                                      previousAdAccountIdRef.current = null;
                                    }
                                    // Persist immediately after page selection
                                    const payload = {
                                      page: page,
                                      adAccount: isPageChanged
                                        ? null
                                        : selectedAdAccount,
                                      instagram: isPageChanged
                                        ? null
                                        : selectedInstagram,
                                      pixel: isPageChanged
                                        ? null
                                        : selectedPixel,
                                    };
                                    localStorage.setItem(
                                      "metaAdsAccountData",
                                      JSON.stringify(payload),
                                    );

                                    // Dispatch custom event
                                    window.dispatchEvent(
                                      new Event("metaAdsSelectionsChanged"),
                                    );
                                  }}
                                >
                                  <div className="relative w-[19.6px] h-[19.6px] bg-[#0201021a] rounded-[700px] overflow-hidden flex items-center justify-center flex-shrink-0">
                                    {page.picture?.data?.url && (
                                      <img
                                        className="w-full h-full object-cover"
                                        alt={page.name}
                                        src={page.picture.data.url}
                                      />
                                    )}
                                  </div>
                                  <span className="relative w-fit font-medium text-variable-collection-black text-[11.2px] tracking-[0] leading-[normal]">
                                    {page.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="relative self-stretch font-normal text-[#878da1] text-xs tracking-[0] leading-snug">
                          Select the Page you want to run ads from
                        </p>
                      </div>

                      {/* Ad Account */}
                      <div className="flex flex-col items-start gap-[8.4px] relative">
                        <label className="relative w-fit font-normal text-white text-sm tracking-[0] leading-[18.2px] whitespace-nowrap">
                          Choose your Ad Account
                        </label>

                        <div className="relative self-stretch w-full ad-account-dropdown">
                          <div
                            className={`flex h-11 items-center justify-between px-[11.2px] py-[4.2px] relative self-stretch w-full bg-white rounded-[8.4px] overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${!adAccounts || adAccounts.length === 0 ? "cursor-default" : ""}`}
                            onClick={() =>
                              !isLoadingAdAccounts &&
                              !isAdAccountsFetching &&
                              adAccounts &&
                              adAccounts.length > 0 &&
                              setIsAdAccountDropdownOpen(
                                !isAdAccountDropdownOpen,
                              )
                            }
                          >
                            <div className="inline-flex items-center relative flex-[0_0_auto] min-w-0 flex-1">
                              <span className="relative min-w-0 truncate font-medium text-variable-collection-black text-[12px] sm:text-[11.2px] tracking-[0] leading-[normal]">
                                {isLoadingAdAccounts || isAdAccountsFetching
                                  ? "Loading..."
                                  : selectedAdAccountId
                                    ? (() => {
                                        const displayAccountId =
                                          selectedAdAccountDisplay?.account_id ||
                                          selectedAdAccountDisplay?.id;
                                        return displayAccountId
                                          ? `${selectedAdAccountDisplay?.name} [${displayAccountId}]`
                                          : selectedAdAccountDisplay?.name ||
                                              "";
                                      })()
                                    : adAccounts && adAccounts.length > 0
                                      ? "Select an ad account"
                                      : "No accounts found"}
                              </span>
                            </div>

                            {adAccounts &&
                              adAccounts.length > 0 &&
                              !isLoadingAdAccounts &&
                              !isAdAccountsFetching && (
                                <img
                                  className={`relative w-[22.4px] h-[22.4px] aspect-[1] transition-transform flex-shrink-0 ${isAdAccountDropdownOpen ? "rotate-180" : ""}`}
                                  alt=""
                                  src={agnleDownSmall}
                                />
                              )}
                          </div>

                          {isAdAccountDropdownOpen &&
                            adAccounts &&
                            adAccounts.length > 0 && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-[8.4px] shadow-lg z-10 max-h-[200px] overflow-y-auto">
                                {adAccounts.map((account: any) => (
                                  <div
                                    key={account.id}
                                    className={`flex items-center px-[11.2px] py-[8px] cursor-pointer hover:bg-gray-100 ${
                                      selectedAdAccountId === account.id
                                        ? "bg-gray-100"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      const cleanedAccountName = (
                                        account.name || ""
                                      )
                                        .replace(
                                          /\s*\[(?:act_[^\]]+|\d+)\]/g,
                                          "",
                                        )
                                        .replace(/^\s*(?:act_)?\d+\s+/, "")
                                        .trim();
                                      const accountSelection = {
                                        ...account,
                                        name:
                                          cleanedAccountName || account.name,
                                      };
                                      setSelectedAdAccountId(account.id);
                                      setSelectedAdAccount(accountSelection);
                                      setIsAdAccountDropdownOpen(false);
                                      // Persist immediately after ad account selection
                                      const payload = {
                                        page: selectedPage,
                                        adAccount: accountSelection,
                                        instagram: selectedInstagram,
                                        pixel: selectedPixel,
                                      };
                                      localStorage.setItem(
                                        "metaAdsAccountData",
                                        JSON.stringify(payload),
                                      );
                                      // Dispatch custom event
                                      window.dispatchEvent(
                                        new Event("metaAdsSelectionsChanged"),
                                      );
                                    }}
                                  >
                                    <span className="relative w-fit font-medium text-variable-collection-black text-[11.2px] tracking-[0] leading-[normal]">
                                      {account.account_id
                                        ? `${account.name} [${account.account_id}]`
                                        : account.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                        <p className="relative self-stretch font-normal text-[#878da1] text-xs tracking-[0] leading-snug">
                          Make sure it has permissions to your Page
                        </p>
                      </div>

                      {/* Instagram Account */}
                      <div className="flex flex-col items-start gap-[8.4px] relative">
                        <label className="relative w-fit font-normal text-white text-sm tracking-[0] leading-[18.2px] whitespace-nowrap">
                          Select Instagram Account
                        </label>

                        <div className="relative self-stretch w-full instagram-account-dropdown">
                          <div
                            className={`flex h-11 items-center justify-between px-[11.2px] py-[4.2px] relative self-stretch w-full bg-white rounded-[8.4px] overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${!instagramAccounts || instagramAccounts.length === 0 ? "cursor-default" : ""}`}
                            onClick={() => {
                              const accounts =
                                instagramData?.instagramAccounts ||
                                instagramData?.accounts ||
                                instagramData?.instagram_accounts;
                              if (
                                !isLoadingInstagram &&
                                !isRetryingInstagram &&
                                !isInstagramFetching &&
                                !isInstagramError &&
                                accounts &&
                                accounts.length > 0
                              ) {
                                setIsInstagramDropdownOpen(
                                  !isInstagramDropdownOpen,
                                );
                              }
                            }}
                          >
                            <div className="inline-flex items-center gap-[8.4px] relative flex-[0_0_auto] min-w-0 flex-1">
                              {!isLoadingInstagram && selectedInstagramId && (
                                <div className="relative w-[19.6px] h-[19.6px] bg-[#0201021a] rounded-[700px] overflow-hidden flex items-center justify-center flex-shrink-0">
                                  {(() => {
                                    const profilePic =
                                      selectedInstagramDisplay?.profile_picture_url;
                                    return (
                                      profilePic && (
                                        <img
                                          className="w-full h-full object-cover"
                                          alt={
                                            selectedInstagramDisplay?.name ||
                                            "Instagram"
                                          }
                                          src={profilePic}
                                        />
                                      )
                                    );
                                  })()}
                                </div>
                              )}
                              <span className="relative min-w-0 truncate font-medium text-variable-collection-black text-[12px] sm:text-[11.2px] tracking-[0] leading-[normal]">
                                {isLoadingInstagram ||
                                isInstagramFetching ||
                                isRetryingInstagram
                                  ? "Loading..."
                                  : selectedInstagramId
                                    ? selectedInstagramDisplay?.username ||
                                      selectedInstagramDisplay?.name ||
                                      ""
                                    : isInstagramError
                                      ? "Failed to load"
                                      : instagramAccounts &&
                                          instagramAccounts.length > 0
                                        ? "Select Account"
                                        : "No accounts found"}
                              </span>
                            </div>

                            {(() => {
                              const accounts =
                                instagramData?.instagramAccounts ||
                                instagramData?.accounts ||
                                instagramData?.instagram_accounts;
                              return (
                                (selectedInstagramId ||
                                  (accounts && accounts.length > 0)) &&
                                !isLoadingInstagram && (
                                  <img
                                    className={`relative w-[22.4px] h-[22.4px] aspect-[1] transition-transform flex-shrink-0 ${isInstagramDropdownOpen ? "rotate-180" : ""}`}
                                    alt=""
                                    src={agnleDownSmall}
                                  />
                                )
                              );
                            })()}
                          </div>

                          {isInstagramDropdownOpen &&
                            instagramAccounts &&
                            instagramAccounts.length > 0 && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-[8.4px] shadow-lg z-10 max-h-[200px] overflow-y-auto">
                                {instagramAccounts.map((account: any) => {
                                  const displayName =
                                    account.username ||
                                    account.pageName ||
                                    account.name ||
                                    "Instagram Account";
                                  return (
                                    <div
                                      key={account.id}
                                      className={`flex items-center gap-[8.4px] px-[11.2px] py-[8px] cursor-pointer hover:bg-gray-100 ${
                                        selectedInstagramId === account.id
                                          ? "bg-gray-100"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        const instagramAccount = {
                                          id: account.id,
                                          name:
                                            account.name ||
                                            account.username ||
                                            "",
                                          profile_picture_url:
                                            account.profile_picture_url,
                                          username: account.username,
                                        };
                                        setSelectedInstagramId(account.id);
                                        setSelectedInstagram(instagramAccount);
                                        setIsInstagramDropdownOpen(false);
                                        // Persist immediately after instagram selection
                                        const payload = {
                                          page: selectedPage,
                                          adAccount: selectedAdAccount,
                                          instagram: instagramAccount,
                                          pixel: selectedPixel,
                                        };
                                        localStorage.setItem(
                                          "metaAdsAccountData",
                                          JSON.stringify(payload),
                                        );
                                        // Dispatch custom event
                                        window.dispatchEvent(
                                          new Event("metaAdsSelectionsChanged"),
                                        );
                                      }}
                                    >
                                      <div className="relative w-[19.6px] h-[19.6px] bg-[#0201021a] rounded-[700px] overflow-hidden flex items-center justify-center flex-shrink-0">
                                        {account.profile_picture_url && (
                                          <img
                                            className="w-full h-full object-cover"
                                            alt={displayName}
                                            src={account.profile_picture_url}
                                          />
                                        )}
                                      </div>
                                      <span className="relative w-fit font-medium text-variable-collection-black text-[11.2px] tracking-[0] leading-[normal]">
                                        {displayName}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                        <p className="relative self-stretch font-normal text-[#878da1] text-xs tracking-[0] leading-snug">
                          {isInstagramError
                            ? "Instagram API permission issue - this field is optional"
                            : "Linked to your Facebook Business Manager"}
                        </p>
                      </div>

                      {/* Pixel */}
                      <div className="flex flex-col items-start gap-[8.4px] relative">
                        <label
                          className={`relative w-fit font-normal text-sm tracking-[0] leading-[18.2px] whitespace-nowrap ${
                            !selectedAdAccountId || isLoadingPixelsForNewAccount
                              ? "text-[#878da1]"
                              : "text-white"
                          }`}
                        >
                          Choose or connect a Pixel
                        </label>

                        <div className="relative self-stretch w-full pixel-dropdown">
                          <div
                            className={`flex h-11 items-center justify-between px-[11.2px] py-[4.2px] relative self-stretch w-full rounded-[8.4px] overflow-hidden transition-all ${
                              !selectedAdAccountId ||
                              isLoadingPixelsForNewAccount
                                ? "bg-[#b3b3b3] border border-transparent cursor-not-allowed select-none"
                                : "bg-white cursor-pointer hover:opacity-90 border-transparent"
                            }`}
                            onClick={() =>
                              selectedAdAccountId &&
                              !isLoadingPixelsForNewAccount &&
                              !isLoadingAdAccountPixels &&
                              adAccountPixelsData?.pixels &&
                              adAccountPixelsData.pixels.length > 0 &&
                              setIsPixelDropdownOpen(!isPixelDropdownOpen)
                            }
                          >
                            <div className="inline-flex items-center relative flex-[0_0_auto] min-w-0 flex-1">
                              <span
                                className={`relative min-w-0 truncate font-medium text-[12px] sm:text-[11.2px] tracking-[0] leading-[normal] ${
                                  !selectedAdAccountId ||
                                  isLoadingPixelsForNewAccount
                                    ? "text-[#0b0b0b]"
                                    : "text-variable-collection-black"
                                }`}
                              >
                                {isLoadingPixelsForNewAccount ||
                                isLoadingAdAccountPixels
                                  ? "Loading..."
                                  : !selectedAdAccountId
                                    ? "Select an ad account first"
                                    : selectedPixelId
                                      ? (() => {
                                          const pixelId =
                                            selectedPixelDisplay?.id;
                                          return selectedPixelDisplay
                                            ? `${selectedPixelDisplay.name}${pixelId ? ` [${pixelId}]` : ""}`
                                            : "";
                                        })()
                                      : adAccountPixelsData?.pixels &&
                                          adAccountPixelsData.pixels.length > 0
                                        ? "Select a pixel"
                                        : "No Pixel Found"}
                              </span>
                            </div>

                            {adAccountPixelsData?.pixels &&
                              adAccountPixelsData.pixels.length > 0 &&
                              !isLoadingAdAccountPixels &&
                              !isLoadingPixelsForNewAccount && (
                                <img
                                  className={`relative w-[22.4px] h-[22.4px] aspect-[1] transition-transform flex-shrink-0 ${isPixelDropdownOpen ? "rotate-180" : ""}`}
                                  alt=""
                                  src={agnleDownSmall}
                                />
                              )}
                          </div>

                          {isPixelDropdownOpen &&
                            adAccountPixelsData?.pixels &&
                            adAccountPixelsData.pixels.length > 0 &&
                            !isLoadingPixelsForNewAccount && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-[8.4px] shadow-lg z-10 max-h-[200px] overflow-y-auto">
                                {adAccountPixelsData.pixels.map(
                                  (pixel: any) => (
                                    <div
                                      key={pixel.id || pixel.pixelId}
                                      className={`flex items-center px-[11.2px] py-[8px] cursor-pointer hover:bg-gray-100 ${
                                        selectedPixelId ===
                                        (pixel.id || pixel.pixelId)
                                          ? "bg-gray-100"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        const pixelAccount = {
                                          id: pixel.id || pixel.pixelId,
                                          name: pixel.name,
                                        };
                                        setSelectedPixelId(
                                          pixel.id || pixel.pixelId,
                                        );
                                        setSelectedPixel(pixelAccount);
                                        setIsPixelDropdownOpen(false);
                                        // Persist immediately after pixel selection
                                        const payload = {
                                          page: selectedPage,
                                          adAccount: selectedAdAccount,
                                          instagram: selectedInstagram,
                                          pixel: pixelAccount,
                                        };
                                        localStorage.setItem(
                                          "metaAdsAccountData",
                                          JSON.stringify(payload),
                                        );
                                        // Dispatch custom event
                                        window.dispatchEvent(
                                          new Event("metaAdsSelectionsChanged"),
                                        );
                                      }}
                                    >
                                      <span className="relative w-fit font-medium text-variable-collection-black text-[11.2px] tracking-[0] leading-[normal]">
                                        {`${pixel.name}${pixel.id ? ` [${pixel.id}]` : ""}`}
                                      </span>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                        </div>
                        <p className="relative self-stretch font-normal text-transparent text-xs tracking-[0] leading-[12.7px]">
                          <span className="text-[#878da1]">
                            Select a pixel for tracking
                          </span>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {!showInlineReconnect && (
                <>
                  {/* ── Step 3: Pixel Tracking (Exact design from main page) ── */}
                  <div className="flex flex-col items-start gap-2.5 ml-[-3px] relative self-stretch w-full lg:max-w-[900px] flex-[0_0_auto] z-10 mt-8">
                    <div className="flex flex-col items-start gap-6 sm:gap-8 relative self-stretch w-full flex-[0_0_auto]">
                      {/* Step 3 content */}
                      <div className="flex flex-col items-start gap-4 sm:gap-6 w-full px-0 sm:px-4 md:pl-14 md:pr-0 lg:pl-[52px] lg:pr-0">
                        {selectedPixelId && (
                          <PixelCapiTrackingSection
                            showIntegrationReady={Boolean(
                              isPixelAlreadySaved &&
                              (editingPixelData?.capiToken || "").trim(),
                            )}
                            capiTokenValue={editingPixelData?.capiToken || ""}
                            onCapiTokenChange={handleCapiTokenChange}
                            capiTokenDisabled={!selectedAdAccountId}
                            isHowToOpen={isCapiHowToOpen}
                            onToggleHowTo={() =>
                              setIsCapiHowToOpen((prev) => !prev)
                            }
                            onSave={() => setIsCapiTokenConfirmOpen(true)}
                            saveDisabled={
                              isCreatingPixel ||
                              isUpdatingPixel ||
                              !editingPixelData ||
                              !selectedAdAccountId
                            }
                            isSaving={isCreatingPixel || isUpdatingPixel}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isAccountGuideModalOpen && (
        <div
          className="fixed inset-0 z-[100002]  bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-hidden px-3 py-4 sm:p-8"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setIsAccountGuideModalOpen(false);
            }
          }}
        >
          <div className="relative w-full max-w-[920px] h-[calc(100dvh-32px)] sm:h-[94vh] lg:h-[97vh] rounded-[30px] lg:rounded-[40px] p-[2px] overflow-hidden bg-gradient-to-b from-[#1B76FF] via-[#001AFF] to-white shadow-[0px_10px_40px_0px_#060C1180]">
            <div className="relative w-full h-full bg-[#0B0F14] rounded-[30px] lg:rounded-[38px] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="relative px-[32px] pb-[calc(64px+env(safe-area-inset-bottom))] pt-[64px] lg:px-[64px] lg:pt-[80px] lg:pb-[calc(64px+env(safe-area-inset-bottom))]">
                {/* Close icon scrolls with content (not sticky) */}
                <button
                  type="button"
                  onClick={() => setIsAccountGuideModalOpen(false)}
                  className="absolute top-8 right-8 lg:top-8 lg:right-[63.5px] z-20 text-white/80  transition-colors text-[26px] leading-none"
                  aria-label="Close"
                >
                  ✕
                </button>
                <MetaAdsFullGuide
                  isOpen
                  onToggle={() => {}}
                  onReachOutToUs={() => {
                    setIsAccountGuideModalOpen(false);
                    window.location.href = ROUTES.CONTACT;
                  }}
                  toggleContainerClassName="hidden"
                  contentContainerClassName="mt-0 w-full px-0"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (typeof window === "undefined") return null;
  return createPortal(modalContent, document.body);
}
